import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  getFRlowReader,
  getFlowsDatatable,
  createFlow,
  createFlowV2,
  editFlow,
  editFlowV2,
  setFlowContentEntityTypeNavigations,
  getFlowDetailsById,
  cloneFlow,
  getFlowDetailsByIdV2,
  getFlowsLookups,
  getSelectionFlow,
} from './thunks';
import { FlowsState } from './types';

export const name = 'Flows';

const initialState: FlowsState = {
  flowReader: null,
  flowReaderStatus: 'idle',
  flowsDatatable: {
    data: null,
    status: 'idle',
  },
  createFlow: {
    status: 'idle',
    data: null,
  },
  createFlowV2: {
    status: 'idle',
    data: null,
  },
  editFlow: {
    status: 'idle',
  },
  editFlowV2: {
    data: null,
    status: 'idle',
  },
  setFlowContentEntityTypeNavigations: {
    status: 'idle',
  },
  flowDetailsById: {
    data: null,
    status: 'idle',
  },
  flowDetailsByIdV2: {
    data: null,
    status: 'idle',
  },
  cloneFlow: {
    status: 'idle',
    data: null,
  },
  flowsLookups: {
    status: 'idle',
    data: [],
  },
  selectionFlow: {
    status: 'idle',
    data: null,
  },
};

export const getFRlowReaderAsync = createAsyncThunk(
  name + '/getFRlowReader',
  getFRlowReader
);

export const getFlowsDatatableAsync = createAsyncThunk(
  name + '/getFlowsDatatable',
  getFlowsDatatable
);

export const createFlowAsync = createAsyncThunk(
  name + '/createFlow',
  createFlow
);

export const createFlowV2Async = createAsyncThunk(
  name + '/createFlowV2',
  createFlowV2
);

export const cloneFlowAsync = createAsyncThunk(name + '/cloneFlow', cloneFlow);

export const editFlowAsync = createAsyncThunk(name + '/editFlow', editFlow);

export const editFlowV2Async = createAsyncThunk(
  name + '/editFlowV2',
  editFlowV2
);

export const setFlowContentEntityTypeNavigationsAsync = createAsyncThunk(
  name + '/setFlowContentEntityTypeNavigations',
  setFlowContentEntityTypeNavigations
);

export const getFlowDetailsByIdAsync = createAsyncThunk(
  name + '/getFlowDetailsById',
  getFlowDetailsById
);

export const getFlowDetailsByIdV2Async = createAsyncThunk(
  name + '/getFlowDetailsByIdV2',
  getFlowDetailsByIdV2
);

export const getFlowsLookupsAsync = createAsyncThunk(
  name + '/getFlowsLookups',
  getFlowsLookups
);

export const getSelectionFlowAsync = createAsyncThunk(
  name + '/getSelectionFlow',
  getSelectionFlow
);

export const slice = createSlice({
  name: name,
  initialState,
  reducers: {
    clear: () => {
      return initialState;
    },
    setFlowReader: (state, action) => {
      state.flowReader = action.payload;
    },
    removeFlowReader: (state) => {
      state.flowReader = null;
    },
    clearFlowsDatatable: (state) => {
      state.flowsDatatable = initialState.flowsDatatable;
    },
    clearSelectionFlow: (state) => {
      state.selectionFlow.data = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getFRlowReaderAsync.pending, (state) => {
        state.flowReaderStatus = 'loading';
      })
      .addCase(getFRlowReaderAsync.fulfilled, (state, action) => {
        state.flowReaderStatus = 'idle';
        state.flowReader = action.payload;
      })
      .addCase(getFRlowReaderAsync.rejected, (state) => {
        state.flowReaderStatus = 'failed';
      })
      .addCase(getFlowsDatatableAsync.pending, (state) => {
        state.flowsDatatable.status = 'loading';
      })
      .addCase(getFlowsDatatableAsync.fulfilled, (state, action) => {
        state.flowsDatatable = {
          data: action.payload,
          status: 'idle',
        };
      })
      .addCase(getFlowsDatatableAsync.rejected, (state) => {
        state.flowsDatatable.status = 'failed';
      })
      .addCase(createFlowAsync.pending, (state) => {
        state.createFlow.status = 'loading';
      })
      .addCase(createFlowAsync.fulfilled, (state, action) => {
        state.createFlow = {
          data: action.payload,
          status: 'idle',
        };
      })
      .addCase(createFlowAsync.rejected, (state) => {
        state.createFlow.status = 'failed';
      })
      .addCase(createFlowV2Async.pending, (state) => {
        state.createFlowV2.status = 'loading';
      })
      .addCase(createFlowV2Async.fulfilled, (state, action) => {
        state.createFlowV2 = {
          data: action.payload,
          status: 'idle',
        };
      })
      .addCase(createFlowV2Async.rejected, (state) => {
        state.createFlowV2.status = 'failed';
      })
      .addCase(editFlowAsync.fulfilled, (state) => {
        state.editFlow.status = 'idle';
      })
      .addCase(editFlowAsync.rejected, (state) => {
        state.editFlow.status = 'failed';
      })
      .addCase(editFlowAsync.pending, (state) => {
        state.editFlow.status = 'loading';
      })
      .addCase(editFlowV2Async.fulfilled, (state) => {
        state.editFlowV2.status = 'idle';
      })
      .addCase(editFlowV2Async.rejected, (state) => {
        state.editFlowV2.status = 'failed';
      })
      .addCase(editFlowV2Async.pending, (state) => {
        state.editFlowV2.status = 'loading';
      })
      .addCase(setFlowContentEntityTypeNavigationsAsync.pending, (state) => {
        state.setFlowContentEntityTypeNavigations.status = 'loading';
      })
      .addCase(setFlowContentEntityTypeNavigationsAsync.fulfilled, (state) => {
        state.setFlowContentEntityTypeNavigations.status = 'idle';
      })
      .addCase(setFlowContentEntityTypeNavigationsAsync.rejected, (state) => {
        state.setFlowContentEntityTypeNavigations.status = 'failed';
      })
      .addCase(getFlowDetailsByIdAsync.pending, (state) => {
        state.flowDetailsById.status = 'loading';
      })
      .addCase(getFlowDetailsByIdAsync.fulfilled, (state, action) => {
        state.flowDetailsById = {
          data: action.payload,
          status: 'idle',
        };
      })
      .addCase(getFlowDetailsByIdAsync.rejected, (state) => {
        state.flowDetailsById.status = 'failed';
      })
      .addCase(getFlowDetailsByIdV2Async.pending, (state) => {
        state.flowDetailsByIdV2.status = 'loading';
      })
      .addCase(getFlowDetailsByIdV2Async.fulfilled, (state, action) => {
        state.flowDetailsByIdV2 = {
          data: action.payload,
          status: 'idle',
        };
      })
      .addCase(getFlowDetailsByIdV2Async.rejected, (state) => {
        state.flowDetailsByIdV2.status = 'failed';
      })
      .addCase(cloneFlowAsync.pending, (state) => {
        state.cloneFlow.status = 'loading';
      })
      .addCase(cloneFlowAsync.fulfilled, (state, action) => {
        state.cloneFlow = {
          data: action.payload,
          status: 'idle',
        };
      })
      .addCase(cloneFlowAsync.rejected, (state) => {
        state.cloneFlow.status = 'failed';
      })
      .addCase(getFlowsLookupsAsync.pending, (state) => {
        state.flowsLookups.status = 'loading';
      })
      .addCase(getFlowsLookupsAsync.fulfilled, (state, action) => {
        state.flowsLookups = {
          data: action.payload,
          status: 'idle',
        };
      })
      .addCase(getFlowsLookupsAsync.rejected, (state) => {
        state.flowsLookups.status = 'failed';
      })
      .addCase(getSelectionFlowAsync.pending, (state) => {
        state.selectionFlow.status = 'loading';
      })
      .addCase(getSelectionFlowAsync.fulfilled, (state, action) => {
        state.selectionFlow = {
          data: action.payload,
          status: 'idle',
        };
      })
      .addCase(getSelectionFlowAsync.rejected, (state) => {
        state.selectionFlow.status = 'failed';
      });
  },
});

export const actions = slice.actions;

export default slice.reducer;
